import axios from "axios";
import { Message, MessageBox } from "element-ui";
import store from "@/store";
import { config } from "./config";
import errorCode from "./errorCode";
import {
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setToken,
  getVisitTenantId,
} from "@/utils/auth";
import { signature } from "./sign";

const { result_code, base_url, request_timeout } = config;
// 需要忽略的提示。忽略后，自动 Promise.reject('error')
const ignoreMsgs = [
  "无效的刷新令牌", // 刷新令牌被删除时，不用提示
  "刷新令牌已过期", // 使用刷新令牌，刷新获取新的访问令牌时，结果因为过期失败，此时需要忽略。否则，会导致继续 401，无法跳转到登出界面
];

// 是否显示重新登录
export let isRelogin = { show: false };
// 请求队列
let requestList = [];
// 是否正在刷新中
let isRefreshToken = false;

axios.defaults.headers["Content-Type"] = "application/json;charset=utf-8";

class Request {
  constructor(baseURL, timeout = 10000) {
    this.instance = axios.create({
      baseURL,
      timeout,
    });

    this.instance.interceptors.request.use(
      (config) => {
        const signObj = signature();
        config.headers["timestamp"] = signObj.timestamp;
        config.headers["nonce"] = signObj.timestamp;
        config.headers["appid"] = signObj.appid;
        config.headers["sign"] = signObj.sign;
        // 设置 token：未登录时不带 Authorization
        // （原实现用 "Bearer test1" 兜底，在服务端开启模拟登录（yudao.security.mock-enable）
        //   时会被当成 userId=1 登录，属于越权隐患；未登录接口本来就不需要 token）
        if (getAccessToken()) {
          config.headers["Authorization"] = "Bearer " + getAccessToken();
        }
        // 设置租户
        config.headers["tenant-id"] = 88;
        return config;
      },
      (err) => {
        return err;
      },
    );
    this.instance.interceptors.response.use(
      (res) => {
        let { data } = res;
        if (!data) {
          console.log("请求没有返回值");
          // 返回“[HTTP]请求没有返回值”;
          throw new Error();
        }
        const code = data.code || result_code;
        // 获取错误信息
        const msg = data.msg || errorCode[code] || errorCode["default"];
        if (ignoreMsgs.indexOf(msg) !== -1) {
          // 如果是忽略的错误码，直接返回 msg 异常
          Message.error({
            offset: 300,
            dangerouslyUseHTMLString: true,
            message: msg,
          });
          return Promise.reject(msg);
        } else if (code === 401) {
          console.log("刷新token");
          this.handleAuthorized()
        } else if (code === 500) {
          Message.error({
            offset: 300,
            dangerouslyUseHTMLString: true,
            message: msg,
          });
          return Promise.reject(new Error(msg));
        } else if (code === 901) {
          Message.error({
            offset: 300,
            dangerouslyUseHTMLString: true,
            message: msg,
          });
          return Promise.reject(new Error(msg));
        } else if (code !== 0 && code !== 200) {
          if (msg === "无效的刷新令牌") {
            // hard coding：忽略这个提示，直接登出
            console.log(msg);
          } else {
            Message.error(msg);
          }
          return Promise.reject("error");
        } else {
          return res;
        }
      },
      (err) => {
        // Notification.error({ title: err });
        return err;
      },
    );
  }

  handleAuthorized() {
    if (!isRelogin.show) {
      isRelogin.show = true;
      MessageBox.confirm(
        "登录状态已过期，您可以继续留在该页面，或者重新登录",
        "系统提示",
        {
          confirmButtonText: "重新登录",
          cancelButtonText: "取消",
          type: "warning",
        },
      )
        .then(() => {
          isRelogin.show = false; 
          localStorage.removeItem("currentExamId");
          store.dispatch("LogOut").then(() => {
            // location.href = getPath("/index");
          });
        })
        .catch(() => {
          isRelogin.show = false;
        });
    }
    // return Promise.reject("无效的会话，或者会话已过期，请重新登录。");
  }

  request(config) {
    return new Promise((resolve, reject) => {
      this.instance
        .request(config)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // method
  async get(option) {
    const res = await this.request({ method: "GET", ...option });
    return res?.data;
  }

  async post(option) {
    const res = await this.request({ method: "POST", ...option });
    return res?.data;
  }

  async postOrigina(option) {
    const res = await this.request({ method: "POST", ...option });
    return res;
  }

  async delete(option) {
    const res = await this.request({ method: "DELETE", ...option });
    return res?.data;
  }

  async put(option) {
    const res = await this.request({ method: "PUT", ...option });
    return res?.data;
  }

  async download(option) {
    const res = await this.request({
      method: "GET",
      responseType: "blob",
      ...option,
    });
    return res;
  }

  async upload(option) {
    option.headersType = "multipart/form-data";
    const res = await this.request({ method: "POST", ...option });
    return res;
  }
}

export default new Request(base_url, request_timeout);
